import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Loader2, Lock, LogIn, User2, Shield, UserCircle2 } from 'lucide-react';
import { authLogin } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const GoogleIcon = (props) => (
  <svg viewBox="0 0 48 48" width="18" height="18" {...props}>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.5 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.1c-2 1.4-4.5 2.3-7.2 2.3-5.3 0-9.8-3.4-11.3-8L6.1 33C9.5 39.6 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.1 4.1-3.9 5.5l6.2 5.1C41.9 35.1 44 30 44 24c0-1.2-.1-2.3-.4-3.5z"/>
  </svg>
);

const LoginPage = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  // Route can be /login (default admin), /login/admin, or /login/employee
  const initialMode = params.mode === 'employee' ? 'employee' : 'admin';
  const [mode, setMode] = useState(initialMode);
  const defaultLanding = mode === 'employee' ? '/hr/me' : '/admin';
  const from = location.state?.from || defaultLanding;

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await authLogin(identifier.trim(), password);
      await refresh();
      toast({ title: 'Welcome back', description: 'Signed in successfully.' });
      // Redirect target depends on the tab the user picked
      const landing = location.state?.from || (mode === 'employee' ? '/hr/me' : '/admin');
      navigate(landing, { replace: true });
    } catch (err) {
      toast({
        title: 'Sign-in failed',
        description: err?.response?.data?.detail || 'Invalid credentials.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#e2ede6] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center mb-6" aria-label="Emerald Corporates — home">
          <img
            src="/emerald-logo.png"
            alt="Emerald Corporates"
            width={220}
            height={148}
            className="inline-block h-24 w-auto object-contain"
            data-testid="login-logo"
          />
        </Link>

        <div className="bg-white border border-black/5 rounded-2xl shadow-sm p-6 lg:p-8">
          <h1
            className="text-3xl text-[#0b1a2e]"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
          >
            Sign in
          </h1>
          <p className="mt-1 text-sm text-[#0b1a2e]/60">
            {mode === 'employee'
              ? 'Access your Employee Self-Service portal.'
              : 'Access the Emerald admin & management portal.'}
          </p>

          {/* Admin vs Employee tabs — same credentials, different landing surface. */}
          <div className="mt-5 grid grid-cols-2 gap-1 p-1 bg-[#f2f7f4] border border-[#0f5c3d]/10 rounded-full text-xs">
            <button
              type="button"
              data-testid="login-tab-admin"
              onClick={() => setMode('admin')}
              className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full font-semibold transition ${
                mode === 'admin' ? 'bg-white text-[#0f5c3d] shadow-sm' : 'text-[#0b1a2e]/60 hover:text-[#0f5c3d]'
              }`}
            >
              <Shield className="w-3.5 h-3.5" /> Admin & Management
            </button>
            <button
              type="button"
              data-testid="login-tab-employee"
              onClick={() => setMode('employee')}
              className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full font-semibold transition ${
                mode === 'employee' ? 'bg-white text-[#0f5c3d] shadow-sm' : 'text-[#0b1a2e]/60 hover:text-[#0f5c3d]'
              }`}
            >
              <UserCircle2 className="w-3.5 h-3.5" /> Employee / Staff
            </button>
          </div>

          <button
            onClick={handleGoogle}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-[#0f5c3d]/20 bg-white text-[#0b1a2e] font-medium text-sm hover:bg-[#f7fbf8] transition"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="my-6 flex items-center gap-3">
            <span className="flex-1 h-px bg-black/10" />
            <span className="text-xs text-[#0b1a2e]/50">or use email &amp; password</span>
            <span className="flex-1 h-px bg-black/10" />
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <label className="block">
              <span className="block text-xs font-semibold tracking-wide uppercase text-[#0b1a2e]/70 mb-1.5">Email or username</span>
              <div className="relative">
                <User2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b1a2e]/50" />
                <input
                  required
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  autoComplete="username"
                  data-testid="login-identifier"
                  className="w-full border border-[#0f5c3d]/15 rounded-lg pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
                  placeholder="you@emeraldcorporates.com"
                />
              </div>
            </label>
            <label className="block">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold tracking-wide uppercase text-[#0b1a2e]/70">Password</span>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-[#0f5c3d] hover:underline">Forgot password?</Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b1a2e]/50" />
                <input
                  required
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  data-testid="login-password"
                  className="w-full border border-[#0f5c3d]/15 rounded-lg pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
                  placeholder="••••••••••"
                />
              </div>
            </label>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#0f5c3d] text-white font-medium text-sm hover:bg-[#0b1a2e] transition disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Signing in…
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4" /> Sign in
                </>
              )}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-[#0b1a2e]/50">
          <Link to="/" className="hover:text-[#0f5c3d]">← Back to website</Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
